import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';

import { SetAiExtractionContext } from '#screens/ai-workbench/ai-extraction-context.js';
import { ModesEditor } from '#screens/modes-editor/modes-editor.js';
import { useTRPC } from '#src/trpc.js';
import { useDataElTagGenerator } from '#src/utils/use-data-el-tag-generator.js';
import { useAutoSave } from '#utils/use-auto-save/use-auto-save.js';
import { getPackageModesContextValue } from './package-modes-context.js';

export const Route = createFileRoute('/_authorization-gate/package-store/$packageFamilyId/packages/$packageId/modes/')({
	staticData: {
		navigation: {
			label: 'Package: Modes',
			keywords: ['modes', 'operating', 'states'],
		},
	},
	loader: ({ context: { queryClient, getPackageOptions } }) => {
		void queryClient.prefetchQuery(getPackageOptions);
		const cached = queryClient.getQueryData(getPackageOptions.queryKey);
		return { pkg: cached?.pkg };
	},
	head: ({ loaderData }) => ({
		meta: [
			{
				title:
					(loaderData?.pkg ? loaderData.pkg.manufacturerPartNumber + ': ' : '') + 'Modes • Package Editor • Cadstrom',
			},
		],
	}),
	component: ModesTab,
});

function ModesTab() {
	const queryClient = useQueryClient();
	const { getPackageOptions } = Route.useRouteContext();
	const trpc = useTRPC();

	const {
		data: { pkg },
	} = useSuspenseQuery(getPackageOptions);

	const { mutateAsync: updatePackage, isPending: updatePackageIsPending } = useMutation(
		trpc.package.update.mutationOptions({
			onSuccess: (res) => {
				// Merge rather than replace: `isConfidential` is derived from the package family's owner and
				// is not in the mutation response, so overwriting the entry would drop it and hide the badge.
				queryClient.setQueryData(getPackageOptions.queryKey, (previous) =>
					previous === undefined ? undefined : { ...previous, pkg: res.pkg },
				);

				setServerModes(res.pkg.modes);
			},
		}),
	);

	const {
		draftState: draftModes,
		setDraftState: setDraftModes,
		setServerState: setServerModes,
		isDirty,
		handleSave,
		saveAreaContainerRef,
	} = useAutoSave({
		initialState: pkg.modes,
		mutationKeys: trpc.package.update.mutationKey(),
		saveFn: async (draftModes) =>
			await updatePackage({
				id: pkg.id,
				data: {
					modes: draftModes,
				},
			}),
	});

	const extractionContextValue = useMemo(
		() =>
			getPackageModesContextValue({
				signals: pkg.signals,
				modes: draftModes,
				updateModes: setDraftModes,
			}),
		[pkg.signals, draftModes, setDraftModes],
	);

	const el = useDataElTagGenerator(ModesTab);
	return (
		<div {...el()} ref={saveAreaContainerRef} className='flex grow basis-0 flex-col gap-2 overflow-auto px-3 py-2'>
			<SetAiExtractionContext value={extractionContextValue} />
			<ModesEditor
				modes={draftModes}
				signals={pkg.signals}
				onChange={setDraftModes}
				showSaveButton={{
					saveDisabled: !isDirty,
					onClickSave: handleSave,
					isSaving: updatePackageIsPending,
				}}
			/>
		</div>
	);
}
